import { useCallback, useMemo } from "react";
import { useSession } from "contexts/AuthContext";
import {
  ProductAvailability,
  ProductAvailabilityCode,
} from "shared/constants/ProductAvailability";
import { FirestoreProductSupplyDetail } from "reedy-data/models";

/**
 * Extracts availability codes from supply details
 * Maps from availability text to standardized codes
 */
const getAvailabilityCodes = (
  supplyDetails: FirestoreProductSupplyDetail[] = [],
): ProductAvailabilityCode[] => {
  if (!supplyDetails || supplyDetails.length === 0) {
    return [];
  }

  // Map each supply detail to its availability code (if available)
  return supplyDetails
    .map((detail) =>
      detail.availability
        ? (ProductAvailability[detail.availability] ?? null)
        : null,
    )
    .filter((code): code is ProductAvailabilityCode => Boolean(code));
};

/**
 * Custom hook to determine product availability status
 * Extracts codes from supply details and determines active status
 */
export const useProductAvailability = () => {
  const { organisation } = useSession();

  // Get the list of inactive status codes from organization settings
  const inactiveProductStatus = useMemo<ProductAvailabilityCode[]>(() => {
    return organisation.settings?.inactiveProductStatus || [];
  }, [organisation.settings]);

  /**
   * Determines if a product is active based directly on its supply details
   * @param supplyDetails The supply details array from a product
   * @returns true if the product is active, false if inactive
   */
  const isProductActive = useCallback(
    (supplyDetails: FirestoreProductSupplyDetail[] = []): boolean => {
      const availabilityCodes = getAvailabilityCodes(supplyDetails);

      // If no codes, product is considered active
      if (!availabilityCodes.length) {
        return true;
      }

      // Check if ANY code indicates an active status
      // (i.e., not in the inactive status list)
      return availabilityCodes.some(
        (code) => !inactiveProductStatus.includes(code),
      );
    },
    [inactiveProductStatus],
  );

  return {
    isProductActive,
  };
};
